import { useState, useCallback } from "react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { X } from "lucide-react";
import type {
  ProfileQueryRequest,
  ProfileSearchColumn,
} from "@/services/api/companies";
import { normalizeNumber } from "./utils";
import { useProfileFilterOptions } from "./hooks/useProfileFilterOptions";

interface CompanyStaffFiltersProps {
  filters: Partial<ProfileQueryRequest>;
  onFilterChange: (
    key: keyof ProfileQueryRequest,
    value: string | number | boolean | string[] | undefined,
  ) => void;
  onClearFilters: () => void;
}

export function CompanyStaffFilters({
  filters,
  onFilterChange,
  onClearFilters,
}: CompanyStaffFiltersProps) {
  const { countries, seniorities, departments } = useProfileFilterOptions();

  const handleSelectChange = useCallback(
    (key: keyof ProfileQueryRequest) =>
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        onFilterChange(key, value === "" ? undefined : value);
      },
    [onFilterChange],
  );

  const handleNumberChange = useCallback(
    (key: keyof ProfileQueryRequest) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFilterChange(key, normalizeNumber(e.target.value));
      },
    [onFilterChange],
  );

  const handleSearchColumnChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      onFilterChange(
        "searchColumn",
        value === "None" ? undefined : (value as ProfileSearchColumn),
      );
    },
    [onFilterChange],
  );

  const handleFakeProbabilityChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      onFilterChange(
        "maxFakeProbability",
        value === "" ? undefined : Number(value),
      );
    },
    [onFilterChange],
  );

  const selectClassName =
    "w-full rounded-md border border-grid-300 px-3 py-2 text-sm focus:border-imperial-500 focus:outline-none focus:ring-1 focus:ring-imperial-500";

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Search Column
          </label>
          <select
            className={selectClassName}
            value={filters.searchColumn || "None"}
            onChange={handleSearchColumnChange}
          >
            <option value="None">None</option>
            <option value="CompanyWorkedAt">Company worked at</option>
            <option value="School">School</option>
            <option value="FullName">Full Name</option>
            <option value="Title">Title</option>
            <option value="Location">Location</option>
            <option value="Country">Country</option>
            <option value="CurrentCompany">Current Company</option>
            <option value="Seniority">Seniority</option>
            <option value="LinkedInUrl">LinkedIn URL</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Search Term
          </label>
          <SearchTermTagInput
            values={filters.searchTerm ?? []}
            onChange={(v) =>
              onFilterChange("searchTerm", v.length > 0 ? v : undefined)
            }
            disabled={!filters.searchColumn || filters.searchColumn === "None"}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Country
          </label>
          <select
            className={selectClassName}
            value={filters.country || ""}
            onChange={handleSelectChange("country")}
          >
            <option value="">All Countries</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Seniority
          </label>
          <select
            className={selectClassName}
            value={filters.seniority || ""}
            onChange={handleSelectChange("seniority")}
          >
            <option value="">All Seniorities</option>
            {seniorities.map((seniority) => (
              <option key={seniority} value={seniority}>
                {seniority}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Department
          </label>
          <select
            className={selectClassName}
            value={filters.department || ""}
            onChange={handleSelectChange("department")}
          >
            <option value="">All Departments</option>
            {departments.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Min F1 Exp (Yrs)
          </label>
          <Input
            type="number"
            min="0"
            placeholder="Min"
            value={filters.minF1ExperienceYears ?? ""}
            onChange={handleNumberChange("minF1ExperienceYears")}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Max F1 Exp (Yrs)
          </label>
          <Input
            type="number"
            min="0"
            placeholder="Max"
            value={filters.maxF1ExperienceYears ?? ""}
            onChange={handleNumberChange("maxF1ExperienceYears")}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Min Total Exp (Yrs)
          </label>
          <Input
            type="number"
            min="0"
            placeholder="Min"
            value={filters.minTotalExperienceYears ?? ""}
            onChange={handleNumberChange("minTotalExperienceYears")}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Max Total Exp (Yrs)
          </label>
          <Input
            type="number"
            min="0"
            placeholder="Max"
            value={filters.maxTotalExperienceYears ?? ""}
            onChange={handleNumberChange("maxTotalExperienceYears")}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-ink-600">
            Fake Likelihood
          </label>
          <select
            className={selectClassName}
            value={filters.maxFakeProbability ?? ""}
            onChange={handleFakeProbabilityChange}
          >
            <option value="">No Filter</option>
            <option value="30">Likely Real (≤30%)</option>
            <option value="50">Probably Real (≤50%)</option>
            <option value="70">Some Risk (≤70%)</option>
            <option value="90">Mostly Suspect (≤90%)</option>
          </select>
        </div>

        <div className="flex items-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="gap-1 text-ink-500 hover:text-ink-700"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}

function SearchTermTagInput({
  values,
  onChange,
  disabled,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState("");

  const addTerm = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed.length < 2) return;
    if (values.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
  };

  const removeTerm = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <div
      className={`rounded-md border border-grid-300 bg-white focus-within:border-imperial-500 focus-within:ring-1 focus-within:ring-imperial-500 ${disabled ? "opacity-50 pointer-events-none" : ""}`}
    >
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2.5 pt-2">
          {values.map((term, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-md bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs font-medium text-blue-800"
            >
              {term}
              {i < values.length - 1 && (
                <span className="text-blue-400 font-normal -mr-0.5 ml-0.5 text-[10px]">
                  OR
                </span>
              )}
              <button
                type="button"
                onClick={() => removeTerm(i)}
                className="ml-0.5 rounded hover:bg-blue-100 p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className="w-full border-0 bg-transparent px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
        placeholder={
          values.length > 0 ? "Add another term..." : "Min 2 characters"
        }
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={disabled}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addTerm();
          }
          if (e.key === "Backspace" && !draft && values.length > 0) {
            removeTerm(values.length - 1);
          }
        }}
        onBlur={addTerm}
      />
    </div>
  );
}
